import React, { useEffect, useState } from "react";
import IntlMessages from "@util/IntlMessages";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { deleteInstanceAttachment, getInstanceAttachments } from "../../../services/api/Api";
import projectConfig from "@projectConfig";
import "./FormAttachments.less";
import { getFileInfo } from "../../../services/restAPI/RestApi";
import { Spin, Message, Button, Upload, Modal } from "../../BP2Components";
import { Icon_Upload, Icon_Upload_Barcode } from "../../BP2Components/Icons/icons";
import { downloadAttachment } from "../MessageButtons/Modals/MailModal";
import { useMessageItem } from "../../../context/MessageItemContext/MessageItemContext";
import { useFormContext } from "../../../context/FormContext/FormContext";

const { hostCloud } = projectConfig.api;

export function base64ToBlob(base64, mimeType) {
   // Decode Base64 string
   const binaryString = atob(base64);
   // Convert binary string to array buffer
   const len = binaryString.length;
   const bytes = new Uint8Array(len);
   for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
   }
   // Create a Blob from the array buffer
   return new Blob([bytes], { type: mimeType });
}

export function getMimeType(filename) {
   const mimeTypes = {
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      svg: "image/svg+xml",
      txt: "text/plain",
      html: "text/html",
      css: "text/css",
      js: "application/javascript",
      json: "application/json",
      pdf: "application/pdf",
      pdf: "application/pdf",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ppt: "application/vnd.ms-powerpoint",
      pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation"
   };

   const extension = filename.split(".").pop().toLowerCase();
   return mimeTypes[extension] || "application/octet-stream"; // Default MIME type
}

const FormAttachments = () => {
   const { instanceId, stepId } = useMessageItem();
   const { setChangelogData } = useFormContext();

   const [fileList, setFileList] = useState();
   const [showModal, setShowModal] = useState(false);
   const [uploadProps, setUploadProps] = useState({
      name: "multipartFile",
      uploadUrl: `${hostCloud}/legacy/file/process?instanceId=${instanceId}&instanceStepId=${stepId}`,
      addBarcode: false,
      async onRemove(file) {
         try {
            Message.loading(intl.formatMessage({ id: "global.loading" }), 0);
            await deleteInstanceAttachment(instanceId, file.id);
            setChangelogData(`Attachment_${file.name}`, file.name, `Remove File`);
            Message.success(intl.formatMessage({ id: "global.done" }));
         } catch {
            Message.error(intl.formatMessage({ id: "global.error" }));
            return false;
         } finally {
            Message.destroy();
         }
      },

      onUpload(file) {
         if (file?.name) {
            setChangelogData(`Attachment_${file.name}`, file.name, `New File`);
            setFileList((prevFileList) => [
               ...prevFileList,
               {
                  url: URL.createObjectURL(file),
                  name: file.name,
                  id: file.name
               }
            ]);
         }
      }
   });

   const intl = useIntl();

   useEffect(() => {
      const getInitialAttachments = async () => {
         try {
            const attachments = (await getInstanceAttachments(instanceId)).data?.getFiles;
            if (!Array.isArray(attachments)) return;
            const filesPromises = await Promise.allSettled(
               attachments.map(async (attachment) => {
                  const fileID = JSON.parse(attachment.DTB_TEXT1)[0].id;
                  const fileInfo = await getFileInfo(fileID);
                  if (fileInfo) {
                     return {
                        uid: attachment.DTB_ID,
                        name: attachment.DTB_NAZWA,
                        status: "done",
                        fileInfo: {
                           ...fileInfo.data
                        }
                     };
                  }
               })
            );
            const promisedFileList = filesPromises
               .map((promise) => promise.status === "fulfilled" && promise.value)
               .filter((file) => typeof file !== "undefined");
            promisedFileList.forEach((file) => {});

            setFileList(promisedFileList);
         } catch {
            setFileList([]);
         }
      };
      getInitialAttachments();
   }, []);

   const barecodeController = (fieldId) => {
      // modal before choosing file to upload, is function configurated for project
      if (projectConfig?.formAttachmentsModal) {
         setShowModal(true);
      } else {
         const getUploadButton = document.getElementById(fieldId);
         getUploadButton.click();
      }
   };

   const uploadWithoutBarcode = (fieldId) => {
      setUploadProps((prevUploadProps) => ({
         ...prevUploadProps,
         addBarcode: false
      }));
      const getUploadButton = document.getElementById(fieldId);
      getUploadButton.click();
      setShowModal(false);
   };

   const uploadWithBarcode = (fieldId) => {
      setUploadProps((prevUploadProps) => ({
         ...prevUploadProps,
         addBarcode: true
      }));

      const getUploadButton = document.getElementById(fieldId);
      getUploadButton.click();
      setShowModal(false);
   };

   const handleDownloadAllFiles = () => {
      fileList.forEach((file, index) => {
         if (file) {
            setTimeout(async () => {
               const blob = base64ToBlob(file.fileInfo.fileData, getMimeType(file.name));
               downloadAttachment(file, blob);
            }, index * 200);
         }
         // The downloads are staggered by index * 200 milliseconds to avoid triggering browser restrictions on simultaneous downloads.
      });
   };

   if (!fileList)
      return (
         <div className="spin-middle">
            <Spin />
         </div>
      );

   return (
      <div className="form-attachments">
         <div className="form-attachments__buttons">
            <Button buttonType="primary" onClick={() => barecodeController("barcode-button-id")}>
               <Icon_Upload
                  type="upload"
                  style={{
                     paddingRight: "5px",
                     display: projectConfig?.formAttachmentsModal ? "none" : "none"
                  }}
               />{" "}
               <IntlMessages id="forms.clickToUpload" />
            </Button>
            <Button onClick={handleDownloadAllFiles}>
               <IntlMessages id="forms.downloadAllFiles" />
            </Button>
            <Upload
               defaultFileList={fileList}
               {...uploadProps}
               className="form-attachments__upload"
            >
               <Button id="barcode-button-id" hidden />
            </Upload>
         </div>
         {fileList.length > 0 && (
            <p className="form-attachments__list-title">
               <IntlMessages id="forms.fileList" />:
            </p>
         )}
         {fileList.length > 0 &&
            fileList.map((file) => {
               if (file) {
                  return (
                     <Upload
                        {...uploadProps}
                        defaultFile={{
                           name: file.name,
                           url:
                              file.url ??
                              URL.createObjectURL(
                                 base64ToBlob(file.fileInfo.fileData, getMimeType(file.name))
                              ),
                           id: file.uid
                        }}
                        key={file.uid}
                     ></Upload>
                  );
               } else {
                  return null;
               }
            })}
         <Modal isOpen={showModal} onCancel={() => setShowModal(false)}>
            <div className="buttons-container">
               <Button
                  onClick={() => uploadWithoutBarcode("barcode-button-id")}
                  className="modal-barcode-button"
               >
                  <Icon_Upload className="upload-icon" />
                  <IntlMessages className="upload-text" id="upload.without.barcode" />
               </Button>

               <Button
                  onClick={() => uploadWithBarcode("barcode-button-id")}
                  className="modal-barcode-button"
               >
                  <Icon_Upload_Barcode className="upload-icon" />
                  <IntlMessages className="upload-text" id="upload.with.barcode" />
               </Button>
            </div>
         </Modal>
      </div>
   );
};

FormAttachments.propTypes = {
   instanceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   stepId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default FormAttachments;
